<template>

    <Toast/>

    <MenuFinancialCompact v-if="userStore.user?.roles?.includes('ROLE_ISORISE')"/>
    <MenuHealthCompact v-else/>

    <div class="px-6 surface-overlay relative">
        <div class="grid">
            <div class="col-12 md:col-12">

                <ul class="list-none py-2 px-0 m-0 flex align-items-center font-medium overflow-x-auto w-full">
                    <li class="pr-3"><a class="cursor-pointer">Operational Clarity</a></li>
                    <li class="px-2"><i class="pi pi-angle-right text-900"></i></li>
                    <li class="px-2"><a class="cursor-pointer text-900 white-space-nowrap">Calendar</a></li>
                </ul>

                <div style="height: 8px;"></div>

                <div class="grid">
                    <div class="col-12 md:col-12">
                        <div class="card p-fluid">
                            <div class="field">
                                <Dropdown v-model="selectedPractitioner" :options="displayPractitioners"
                                          optionLabel="fullName" optionValue="id" placeholder="Select a Practitioner"
                                          @update:modelValue="onTogglePractitioner"/>
                            </div>
                        </div>
                    </div>
                </div>

                <div style="height: 8px;"></div>

                <div class="card p-fluid">

                    <FullCalendar ref="calendarRef" :options="calendarOptions"/>
                    <ProgressSpinner v-if="submitted" style="width: 16px; height: 16px" strokeWidth="6"
                                     fill="transparent" animationDuration=".5s"
                                     aria-label="Custom ProgressSpinner"/>

                    <Dialog
                        v-model:visible="showDialog"
                        :breakpoints="{ '960px': '75vw', '640px': '90vw' }"
                        :style="{ width: '38rem' }"
                        modal
                        closable
                        @onHide="view = ''">

                        <template #header>
                            <span class="text-900 font-semibold text-xl">
                                {{
                                    view === 'new' ? 'Create' : view === 'edit' ? 'Update'
                                        : view === 'invoice' ? 'Invoice' : view === 'display' ? 'View'
                                            : view === 'delete' ? 'Delete' : 'View'
                                }}
                            </span>
                        </template>

                        <div v-if="(view === 'new')">
                            <div class="grid p-fluid formgrid">

                                <div class="col-12 md:col-6 field">
                                    <label for="type" class="text-900 font-semibold">Type</label>
                                    <Dropdown v-model="newAppointment.type" :options="appointmentTypes"
                                              optionLabel="name"
                                              optionValue="code"
                                              placeholder="Select a Type"/>
                                </div>

                                <div v-show="newAppointment.type!=='P'" class="col-12 field">
                                    <label for="client" class="text-900 font-semibold">Client</label>
                                    <AutoComplete placeholder="Search" :dropdown="false" :multiple="false"
                                                  v-model="newAppointment.client" :suggestions="filteredClients"
                                                  @complete="searchClients($event)" @item-select="onClientSelected"
                                                  field="name"/>
                                </div>

                                <div v-show="newAppointment.type !== 'P' && newAppointment.client?.id &&
                                    clientProvisionComponents.length > 0" class="col-12 field">
                                    <label for="practitioner">Provision Component</label>
                                    <AutoComplete placeholder="Search" :dropdown="true" :multiple="false"
                                                  v-model="newAppointment.provisionComponent"
                                                  :suggestions="filteredProvisionComponents"
                                                  @complete="searchProvisionComponentsOnCreateAppointment($event)"
                                                  field="fullName"/>
                                </div>

                                <div v-show="((newAppointment.type!=='P'))"
                                     class="col-12 field">
                                    <label for="practitioner" class="text-900 font-semibold">Practitioner</label>
                                    <AutoComplete placeholder="Search" :dropdown="true" :multiple="false"
                                                  v-model="newAppointment.practitioner"
                                                  :suggestions="filteredPractitioners"
                                                  @complete="searchPractitioners($event)" field="fullName"/>
                                </div>

                                <div v-show="(newAppointment.type!=='P') && (!newAppointment.provisionComponent)"
                                     class="col-12 field">
                                    <label for="billingPlan" class="text-900 font-semibold">Billing Plan</label>
                                    <AutoComplete placeholder="Search" :dropdown="true" :multiple="false"
                                                  v-model="newAppointment.billingPlan"
                                                  :suggestions="filteredBillingPlans"
                                                  @complete="searchBillingPlan($event)" field="name"/>
                                </div>

                                <div v-show="((newAppointment.type!=='P') && (this.locations.length > 1))"
                                     class="col-12 field">
                                    <label for="location" class="text-900 font-semibold">Location</label>
                                    <AutoComplete placeholder="Search" :dropdown="true" :multiple="false"
                                                  v-model="newAppointment.location" :suggestions="filteredLocations"
                                                  @complete="searchLocations($event)" field="name"/>
                                </div>

                                <div class="col-12 field">
                                    <label for="duration" class="text-900 font-semibold">Duration (Minutes)</label>
                                    <InputNumber id="horizontal" v-model="newAppointment.duration" showButtons
                                                 buttonLayout="horizontal"
                                                 :step="5"
                                                 :min="0"
                                                 decrementButtonClass="p-button-danger"
                                                 incrementButtonClass="p-button-success"
                                                 incrementButtonIcon="pi pi-plus" decrementButtonIcon="pi pi-minus"
                                                 mode="decimal"/>
                                </div>

                                <div v-show="newAppointment.type==='P'" class="col-12 field">
                                    <label for="description" class="text-900 font-semibold">Description</label>
                                    <InputText id="description" v-model.trim="newAppointment.description"
                                               required="true"/>
                                </div>

                                <div class="col-12 md:col-6 field">
                                    <label for="frequency" class="text-900 font-semibold">Frequency</label>
                                    <Dropdown v-model="newAppointment.frequency" :options="frequencyTypes"
                                              optionLabel="name"
                                              optionValue="code"
                                              placeholder="Select a Frequency"/>
                                </div>

                                <div v-show="newAppointment.frequency!=='N'" class="col-12 md:col-6 field">
                                    <label for="frequencyLength" class="text-900 font-semibold">Number of
                                        Occurrences</label>
                                    <InputNumber id="horizontal" v-model="newAppointment.frequencyLength" showButtons
                                                 buttonLayout="horizontal"
                                                 :step="1"
                                                 :min="1"
                                                 decrementButtonClass="p-button-danger"
                                                 incrementButtonClass="p-button-success"
                                                 incrementButtonIcon="pi pi-plus" decrementButtonIcon="pi pi-minus"
                                                 mode="decimal"/>
                                </div>

                                <div class="col-12 field">
                                    <label for="scheduled" class="text-900 font-semibold">Scheduled</label>
                                    <Calendar id="scheduled" v-model="newAppointment.scheduled" required="true"
                                              :showTime="true"
                                              :showSeconds="false" :showIcon="true" dateFormat="yy-mm-dd"/>
                                </div>

                                <div class="col-12 field">
                                    <label for="status" class="text-900 font-semibold">Status</label>
                                    <Dropdown v-model="newAppointment.status" :options="statusTypes" optionLabel="name"
                                              optionValue="code"
                                              placeholder="Select a Status"/>
                                </div>

                                <div v-show="(newAppointment.status === 'S')" class="col-12 md:col-6 field">
                                    <label for="sendCalendarInvites" class="text-900 font-semibold">Send Calendar
                                        Invites?</label>
                                    <Dropdown v-model="newAppointment.sendCalendarInvites" :options="confirmationTypes"
                                              optionLabel="name" optionValue="code"
                                              placeholder="Select a Response"/>
                                </div>

                                <div v-show="(newAppointment.status === 'S')" class="col-12 md:col-6 field">
                                    <label for="sendReminders" class="text-900 font-semibold">Send Reminders?</label>
                                    <Dropdown v-model="newAppointment.sendReminders" :options="confirmationTypes"
                                              optionLabel="name" optionValue="code"
                                              placeholder="Select a Response"/>
                                </div>

                                <div v-show="(newAppointment.status === 'S')" class="col-12 md:col-6 field">
                                    <label for="sendCreationReminder" class="text-900 font-semibold">
                                        Send Creation Reminder?</label>
                                    <Dropdown v-model="newAppointment.sendCreationReminder" :options="confirmationTypes"
                                              optionLabel="name" optionValue="code"
                                              placeholder="Select a Response"/>
                                </div>

                                <div v-show="(newAppointment.status === 'M')" class="col-12 md:col-6 field">
                                    <label for="sendMissedNotice" class="text-900 font-semibold">
                                        Send Missed Appointment Notice?</label>
                                    <Dropdown v-model="newAppointment.sendMissedNotice" :options="confirmationTypes"
                                              optionLabel="name" optionValue="code"
                                              placeholder="Select a Response"/>
                                </div>

                                <div
                                    v-show="((newAppointment.status === 'S') && (this.companyConfiguration.enableOnlineMeetings === 'Y'))"
                                    class="col-12 md:col-6 field">
                                    <label for="setupOnlineMeeting" class="text-900 font-semibold">
                                        Setup Kitrin Online Meeting?</label>
                                    <Dropdown v-model="newAppointment.setupOnlineMeeting" :options="confirmationTypes"
                                              optionLabel="name" optionValue="code"
                                              placeholder="Select a Response"/>
                                </div>

                            </div>
                        </div>

                        <div v-else-if="(view === 'edit')">
                            <div class="grid p-fluid formgrid">

                                <div class="col-12 md:col-6 field">
                                    <label for="type" class="text-900 font-semibold">Type</label>
                                    <Dropdown v-model="appointment.type" :options="appointmentTypes"
                                              optionLabel="name"
                                              optionValue="code"
                                              placeholder="Select a Type"/>
                                </div>

                                <div v-show="appointment.type!=='P'" class="col-12 field">
                                    <label for="client" class="text-900 font-semibold">Client</label>
                                    <AutoComplete placeholder="Search" :dropdown="false" :multiple="false"
                                                  v-model="appointment.clientName" :suggestions="filteredClients"
                                                  @complete="searchClients($event)" @item-select="onClientSelected"
                                                  field="name"/>
                                </div>

                                <div v-show="appointment.type !== 'P' && clientProvisionComponents.length > 0"
                                     class="col-12 field">
                                    <label for="provisionComponent" class="text-900 font-semibold">Provision
                                        Component</label>
                                    <AutoComplete placeholder="Search" :dropdown="true" :multiple="false"
                                                  v-model="appointment.clientProvisionComponentName"
                                                  :suggestions="filteredProvisionComponents"
                                                  @complete="searchProvisionComponentsOnUpdateAppointment($event)"
                                                  field="fullName"/>
                                </div>

                                <div v-show="((appointment.type!=='P'))"
                                     class="col-12 field">
                                    <label for="practitioner" class="text-900 font-semibold">Practitioner</label>
                                    <AutoComplete placeholder="Search" :dropdown="true" :multiple="false"
                                                  v-model="appointment.practitionerName"
                                                  :suggestions="filteredPractitioners"
                                                  @complete="searchPractitioners($event)" field="fullName"/>
                                </div>

                                <div v-show="(appointment.type!=='P') && (!appointment.clientProvisionComponentName)"
                                     class="col-12 field">
                                    <label for="billingPlan" class="text-900 font-semibold">Billing Plan</label>
                                    <AutoComplete placeholder="Search" :dropdown="true" :multiple="false"
                                                  v-model="appointment.billingPlanName"
                                                  :suggestions="filteredBillingPlans"
                                                  @complete="searchBillingPlan($event)" field="name"/>
                                </div>

                                <div v-show="((appointment.type!=='P') && (this.locations.length > 1))"
                                     class="col-12 field">
                                    <label for="location" class="text-900 font-semibold">Location</label>
                                    <AutoComplete placeholder="Search" :dropdown="true" :multiple="false"
                                                  v-model="appointment.locationName" :suggestions="filteredLocations"
                                                  @complete="searchLocations($event)" field="name"/>
                                </div>

                                <div class="col-12 field">
                                    <label for="duration" class="text-900 font-semibold">Duration (Minutes)</label>
                                    <InputNumber id="horizontal" v-model="appointment.duration" showButtons
                                                 buttonLayout="horizontal"
                                                 :step="5"
                                                 :min="0"
                                                 decrementButtonClass="p-button-danger"
                                                 incrementButtonClass="p-button-success"
                                                 incrementButtonIcon="pi pi-plus" decrementButtonIcon="pi pi-minus"
                                                 mode="decimal"/>
                                </div>

                                <div class="col-12 field">
                                    <label for="description" class="text-900 font-semibold">Description</label>
                                    <InputText id="description" v-model.trim="appointment.description"
                                               required="true"/>
                                </div>

                                <div class="col-12 md:col-6 field">
                                    <label for="frequency" class="text-900 font-semibold">Frequency</label>
                                    <Dropdown :disabled="true" v-model="appointment.frequency" :options="frequencyTypes"
                                              optionLabel="name"
                                              optionValue="code"
                                              placeholder="Select a Frequency"/>
                                </div>

                                <div v-show="appointment.frequency!=='N'" class="col-12 md:col-6 field">
                                    <label for="frequencyLength" class="text-900 font-semibold">Number of
                                        Occurrences</label>
                                    <InputNumber :disabled="true" id="horizontal" v-model="appointment.frequencyLength"
                                                 showButtons
                                                 buttonLayout="horizontal"
                                                 :step="1"
                                                 :min="1"
                                                 decrementButtonClass="p-button-danger"
                                                 incrementButtonClass="p-button-success"
                                                 incrementButtonIcon="pi pi-plus" decrementButtonIcon="pi pi-minus"
                                                 mode="decimal"/>
                                </div>

                                <div class="col-12 field">
                                    <label for="scheduled" class="text-900 font-semibold">Scheduled</label>
                                    <Calendar id="scheduled" v-model="parsedScheduled" required="true"
                                              :showTime="true"
                                              :showSeconds="false" :showIcon="true" dateFormat="yy-mm-dd"/>
                                </div>

                                <div class="col-12 md:col-6 field">
                                    <label for="status" class="text-900 font-semibold">Status</label>
                                    <Dropdown v-model="appointment.status" :options="statusTypes" optionLabel="name"
                                              optionValue="code"
                                              placeholder="Select a Status"/>
                                </div>

                                <div v-show="(appointment.status === 'S')" class="col-12 md:col-6 field">
                                    <label for="sendCalendarInvites" class="text-900 font-semibold">Send Calendar
                                        Invites?</label>
                                    <Dropdown v-model="appointment.sendCalendarInvites" :options="confirmationTypes"
                                              optionLabel="name" optionValue="code"
                                              placeholder="Select a Response"/>
                                </div>

                                <div v-show="(appointment.status === 'S')" class="col-12 md:col-6 field">
                                    <label for="sendReminders" class="text-900 font-semibold">Send Reminders?</label>
                                    <Dropdown v-model="appointment.sendReminders" :options="confirmationTypes"
                                              optionLabel="name" optionValue="code"
                                              placeholder="Select a Response"/>
                                </div>

                                <div v-show="(appointment.status === 'S')" class="col-12 md:col-6 field">
                                    <label for="sendCreationReminder" class="text-900 font-semibold">Send Creation
                                        Reminder?</label>
                                    <Dropdown v-model="appointment.sendCreationReminder" :options="confirmationTypes"
                                              optionLabel="name" optionValue="code"
                                              placeholder="Select a Response"/>
                                </div>

                                <div v-show="(appointment.status === 'M')" class="col-12 md:col-6 field">
                                    <label for="sendMissedNotice" class="text-900 font-semibold">Send Missed Appointment
                                        Notice?</label>
                                    <Dropdown v-model="appointment.sendMissedNotice" :options="confirmationTypes"
                                              optionLabel="name" optionValue="code"
                                              placeholder="Select a Response"/>
                                </div>

                                <div
                                    v-show="((appointment.status === 'S') && (this.companyConfiguration.enableOnlineMeetings === 'Y'))"
                                    class="col-12 md:col-6 field">
                                    <label for="setupOnlineMeeting" class="text-900 font-semibold">Setup Kitrin Online
                                        Meeting?</label>
                                    <Dropdown v-model="appointment.setupOnlineMeeting" :options="confirmationTypes"
                                              optionLabel="name" optionValue="code"
                                              placeholder="Select a Response"/>
                                </div>

                            </div>
                        </div>

                        <div v-else-if="(view === 'invoice')">
                            <div class="grid p-fluid formgrid">

                                <div class="col-12 field">
                                    <label for="invoiceType" class="text-900 font-semibold">Invoice Type</label>
                                    <Dropdown v-model="invoiceOptions.type" :options="invoiceTypes"
                                              optionLabel="name"
                                              optionValue="code"
                                              placeholder="Select an Option"/>
                                </div>

                                <div class="col-12 field">
                                    <label for="invoiced" class="text-900 font-semibold">Invoice Date</label>
                                    <Calendar id="invoiced" v-model="parsedInvoiced" required="true"
                                              :showTime="true"
                                              :showSeconds="false" :showIcon="true" dateFormat="yy-mm-dd"/>
                                </div>

                                <div v-if="invoiceOptions.type === 'C'" class="col-12 field">
                                    <label for="startDate" class="text-900 font-semibold">Start Date</label>
                                    <Calendar id="startDate" v-model="invoiceOptions.startDate" required="true"
                                              :showTime="true"
                                              :showSeconds="false" :showIcon="true" dateFormat="yy-mm-dd"/>
                                </div>

                                <div v-if="invoiceOptions.type === 'C'" class="col-12 field">
                                    <label for="endDate" class="text-900 font-semibold">End Date</label>
                                    <Calendar id="endDate" v-model="invoiceOptions.endDate" required="true"
                                              :showTime="true"
                                              :showSeconds="false" :showIcon="true" dateFormat="yy-mm-dd"/>
                                </div>

                                <div v-if="(appointment.type!=='P') && ((bankAccounts) && (bankAccounts.length > 1))"
                                     class="col-12 field">
                                    <label for="bankAccount" class="text-900 font-semibold">Bank Account</label>
                                    <AutoComplete placeholder="Search" :dropdown="true" :multiple="false"
                                                  v-model="invoiceOptions.bankAccount"
                                                  :suggestions="filteredBankAccounts"
                                                  @complete="searchBankAccounts($event)" field="accountHolder"/>
                                </div>

                                <div v-if="(invoiceOptions.type === 'I') && (appointment.type!=='P')"
                                     class="col-12 field">
                                    <label for="billingPlan" class="text-900 font-semibold">Billing Plan</label>
                                    <AutoComplete placeholder="Search" :dropdown="true" :multiple="false"
                                                  v-model="appointment.billingPlanName"
                                                  :suggestions="filteredBillingPlans"
                                                  @complete="searchBillingPlan($event)" field="name"/>
                                </div>

                                <div v-if="(invoiceOptions.type === 'I') && (companyConfiguration.type==='H') && (appointment.clientMedicalName !== null)"
                                     class="col-12 field">
                                    <label for="submissionOption" class="text-900 font-semibold">Invoice Submission
                                        Method</label>
                                    <Dropdown v-model="invoiceOptions.submissionMethod" :options="submissionMethod"
                                              optionLabel="name"
                                              optionValue="code"
                                              placeholder="Select an Option"/>
                                </div>

                                <div v-if="(invoiceOptions.type === 'I') && (companyConfiguration.type==='H') && (appointment.clientMedicalName === null)"
                                     class="col-12 field">
                                    <label for="submissionOption" class="text-900 font-semibold">Invoice Submission
                                        Method</label>
                                    <Dropdown v-model="invoiceOptions.submissionMethod" :options="submissionMethod" :disabled="true"
                                              optionLabel="name"
                                              optionValue="code"
                                              placeholder="Select an Option"/>
                                </div>

                                <div
                                    v-if="(invoiceOptions.type === 'I') && (companyConfiguration.type==='H') && (invoiceOptions.submissionMethod==='B')"
                                    class="col-12 field">
                                    <label for="healthInsuranceSubmission" class="text-900 font-semibold">Claim
                                        Submission Method</label>
                                    <Dropdown v-model="invoiceOptions.healthInsuranceSubmission"
                                              :options="healthInsuranceSubmissionMethod"
                                              optionLabel="name"
                                              optionValue="code"
                                              placeholder="Select an Option"/>
                                </div>

                                <div v-if="(companyConfiguration.type==='H') && (appointment.clientMedicalName !== null)" class="col-12 field">
                                    <label for="useSystemProcedureRates" class="text-900 font-semibold">Use System
                                        Procedure Rates?</label>
                                    <Dropdown v-model="invoiceOptions.useSystemProcedureRates"
                                              :options="confirmationTypes"
                                              optionLabel="name"
                                              optionValue="code"
                                              placeholder="Select an Option"/>
                                </div>

                                <div v-if="(companyConfiguration.type==='H') && (appointment.clientMedicalName === null)" class="col-12 field">
                                    <label v-tooltip="'Option can only be selected if the client has a medical aid'" for="useSystemProcedureRates" class="text-900 font-semibold">Use System
                                        Procedure Rates?</label>
                                    <Dropdown v-model="invoiceOptions.useSystemProcedureRates" :disabled="true"
                                              :options="confirmationTypes"
                                              optionLabel="name"
                                              optionValue="code"
                                              placeholder="Select an Option"/>
                                </div>

                                <div v-if="invoiceOptions.useSystemProcedureRates==='Y'" class="col-12 field">
                                    <label for="rateOption" class="text-900 font-semibold">Rate Option?</label>
                                    <Dropdown v-model="invoiceOptions.rateOption" :options="rateOptionTypes"
                                              optionLabel="name"
                                              optionValue="code"
                                              placeholder="Select an Option"/>
                                </div>

                                <div v-if="company && company.taxRegistered === 'Y'" class="col-12 field">
                                    <label for="useTaxRate" class="text-900 font-semibold">Use Tax Rate?</label>
                                    <Dropdown v-model="invoiceOptions.useTaxRate" :options="confirmationTypes"
                                              optionLabel="name"
                                              optionValue="code"
                                              placeholder="Select an Option"/>
                                </div>


                                <div
                                    v-show="(companyConfiguration.type==='H') && (invoiceOptions.submissionMethod!=='C')"
                                    class="col-12 field">
                                    <label for="useAutomaticSubmission" class="text-900 font-semibold">Automatically
                                        Submit Invoice and/or Claim?</label>
                                    <Dropdown v-model="invoiceOptions.useAutomaticSubmission"
                                              :options="confirmationTypes"
                                              optionLabel="name"
                                              optionValue="code"
                                              placeholder="Select an Option"/>
                                </div>

                                <div
                                    v-show="(companyConfiguration.type!=='H') || (invoiceOptions.submissionMethod==='C')"
                                    class="col-12 field">
                                    <label for="useAutomaticSubmission" class="text-900 font-semibold">Automatically
                                        Submit Invoice?</label>
                                    <Dropdown v-model="invoiceOptions.useAutomaticSubmission"
                                              :options="confirmationTypes"
                                              optionLabel="name"
                                              optionValue="code"
                                              placeholder="Select an Option"/>
                                </div>

                            </div>
                        </div>

                        <div v-else-if="(view === 'display')">
                            <div class="grid p-fluid formgrid">

                                <div class="col-12 md:col-6 field">
                                    <label for="type" class="text-900 font-semibold">Type</label>
                                    <Dropdown :disabled="true" v-model="appointment.type" :options="appointmentTypes"
                                              optionLabel="name"
                                              optionValue="code"
                                              placeholder="Select a Type"/>
                                </div>

                                <div v-show="appointment.type!=='P'" class="col-12 md:col-6 field">
                                    <label for="client" class="text-900 font-semibold">Client</label>
                                    <AutoComplete :disabled="true" placeholder="Search" :dropdown="true"
                                                  :multiple="false"
                                                  v-model="appointment.clientName" :suggestions="filteredClients"
                                                  @complete="searchClients($event)" field="name"/>
                                </div>


                                <div v-show="appointment.type!=='P'" class="col-12 md:col-6 field">
                                    <label for="practitioner" class="text-900 font-semibold">Practitioner</label>
                                    <AutoComplete :disabled="true" placeholder="Search" :dropdown="true"
                                                  :multiple="false"
                                                  v-model="appointment.practitionerName"
                                                  :suggestions="filteredPractitioners"
                                                  @complete="searchPractitioners($event)" field="fullName"/>
                                </div>

                                <div v-show="appointment.type!=='P'" class="col-12 md:col-6 field">
                                    <label for="billingPlan" class="text-900 font-semibold">Billing Plan</label>
                                    <AutoComplete :disabled="true" placeholder="Search" :dropdown="true"
                                                  :multiple="false"
                                                  v-model="appointment.billingPlanName"
                                                  :suggestions="filteredBillingPlans"
                                                  @complete="searchBillingPlan($event)" field="name"/>
                                </div>

                                <div v-show="appointment.type!=='P'" class="col-12 md:col-6 field">
                                    <label for="location" class="text-900 font-semibold">Location</label>
                                    <AutoComplete :disabled="true" placeholder="Search" :dropdown="true"
                                                  :multiple="false"
                                                  v-model="appointment.locationName" :suggestions="filteredLocations"
                                                  @complete="searchLocations($event)" field="name"/>
                                </div>

                                <div v-show="appointment.type!=='P'" class="col-12 md:col-6 field">
                                    <label for="duration" class="text-900 font-semibold">Duration (Minutes)</label>
                                    <InputNumber :disabled="true" id="horizontal" v-model="appointment.duration"
                                                 showButtons
                                                 buttonLayout="horizontal"
                                                 :step="5"
                                                 :min="0"
                                                 decrementButtonClass="p-button-danger"
                                                 incrementButtonClass="p-button-success"
                                                 incrementButtonIcon="pi pi-plus" decrementButtonIcon="pi pi-minus"
                                                 mode="decimal"/>
                                </div>

                                <div v-show="appointment.type==='P'" class="col-12 field">
                                    <label for="description" class="text-900 font-semibold">Description</label>
                                    <InputText :disabled="true" id="description" v-model.trim="appointment.description"
                                               required="true"/>
                                </div>

                                <div class="col-12 field">
                                    <label for="scheduled" class="text-900 font-semibold">Scheduled</label>
                                    <Calendar :disabled="true" id="scheduled" v-model="parsedScheduled" required="true"
                                              :showTime="true"
                                              :showSeconds="false" :showIcon="true" dateFormat="yy-mm-dd"/>
                                </div>

                                <div class="col-12 field">
                                    <label for="status" class="text-900 font-semibold">Status</label>
                                    <Dropdown :disabled="true" v-model="appointment.status" :options="statusTypes"
                                              optionLabel="name"
                                              optionValue="code"
                                              placeholder="Select a Status"/>
                                </div>


                            </div>
                        </div>

                        <div v-else-if="(view === 'delete')">
                            <div class="grid p-fluid formgrid">

                                <div class="col-12 md:col-6 field">
                                    <label for="type" class="text-900 font-semibold">Type</label>
                                    <Dropdown :disabled="true" v-model="appointment.type" :options="appointmentTypes"
                                              optionLabel="name"
                                              optionValue="code"
                                              placeholder="Select a Type"/>
                                </div>

                                <div v-show="appointment.type!=='P'" class="col-12 md:col-6 field">
                                    <label for="client" class="text-900 font-semibold">Client</label>
                                    <AutoComplete :disabled="true" placeholder="Search" :dropdown="true"
                                                  :multiple="false"
                                                  v-model="appointment.clientName" :suggestions="filteredClients"
                                                  @complete="searchClients($event)" field="name"/>
                                </div>


                                <div v-show="appointment.type!=='P'" class="col-12 md:col-6 field">
                                    <label for="practitioner" class="text-900 font-semibold">Practitioner</label>
                                    <AutoComplete :disabled="true" placeholder="Search" :dropdown="true"
                                                  :multiple="false"
                                                  v-model="appointment.practitionerName"
                                                  :suggestions="filteredPractitioners"
                                                  @complete="searchPractitioners($event)" field="fullName"/>
                                </div>

                                <div v-show="appointment.type!=='P'" class="col-12 md:col-6 field">
                                    <label for="billingPlan" class="text-900 font-semibold">Billing Plan</label>
                                    <AutoComplete :disabled="true" placeholder="Search" :dropdown="true"
                                                  :multiple="false"
                                                  v-model="appointment.billingPlanName"
                                                  :suggestions="filteredBillingPlans"
                                                  @complete="searchBillingPlan($event)" field="name"/>
                                </div>

                                <div v-show="appointment.type!=='P'" class="col-12 md:col-6 field">
                                    <label for="location" class="text-900 font-semibold">Location</label>
                                    <AutoComplete :disabled="true" placeholder="Search" :dropdown="true"
                                                  :multiple="false"
                                                  v-model="appointment.locationName" :suggestions="filteredLocations"
                                                  @complete="searchLocations($event)" field="name"/>
                                </div>

                                <div v-show="appointment.type!=='P'" class="col-12 md:col-6 field">
                                    <label for="duration" class="text-900 font-semibold">Duration (Minutes)</label>
                                    <InputNumber :disabled="true" id="horizontal" v-model="appointment.duration"
                                                 showButtons
                                                 buttonLayout="horizontal"
                                                 :step="5"
                                                 :min="0"
                                                 decrementButtonClass="p-button-danger"
                                                 incrementButtonClass="p-button-success"
                                                 incrementButtonIcon="pi pi-plus" decrementButtonIcon="pi pi-minus"
                                                 mode="decimal"/>
                                </div>

                                <div v-show="appointment.type==='P'" class="col-12 field">
                                    <label for="description" class="text-900 font-semibold">Description</label>
                                    <InputText :disabled="true" id="description" v-model.trim="appointment.description"
                                               required="true"/>
                                </div>

                                <div class="col-12 field">
                                    <label for="scheduled" class="text-900 font-semibold">Scheduled</label>
                                    <Calendar :disabled="true" id="scheduled" v-model="parsedScheduled" required="true"
                                              :showTime="true"
                                              :showSeconds="false" :showIcon="true" dateFormat="yy-mm-dd"/>
                                </div>

                            </div>
                        </div>

                        <template #footer>
                            <Button v-if="view === 'delete'" label="Delete All" icon="pi pi-check"
                                    :loading="submitted" @click="deleteAppointmentAll()"
                                    :disabled="!appointment.type"></Button>
                            <Button v-if="view === 'delete'" label="Delete Future" icon="pi pi-check"
                                    :loading="submitted" @click="deleteAppointmentFuture()"
                                    :disabled="!appointment.type"></Button>
                            <Button v-if="view === 'delete'" label="Delete Instance" icon="pi pi-check"
                                    :loading="submitted" @click="deleteAppointment()"
                                    :disabled="!appointment.type"></Button>
                            <Button v-if="view === 'new'" label="Save" icon="pi pi-check"
                                    :disabled="!canCreateAppointment"
                                    :loading="submitted" @click="createAppointment()"></Button>
                            <Button v-if="((view === 'edit') || (view === 'display'))" label="Manage" icon="pi pi-check"
                                    :loading="submitted" @click="manageClient()" :disabled="!appointment.type"></Button>
                            <Button v-if="view === 'edit'" label="Update" icon="pi pi-check"
                                    :loading="submitted" @click="updateAppointment()"
                                    :disabled="!appointment.type"></Button>
                            <Button v-if="view === 'edit'" label="Delete" icon="pi pi-check"
                                    :loading="submitted" @click="callDeleteAppointment()"
                                    :disabled="!appointment.type"></Button>
                            <Button
                                v-if="(view === 'edit') && (this.userConfiguration.enableInvoicing !== 'N') && (appointment.type !== 'P') && (appointment.type !== 'N') && ( (appointment.status === 'C') || (appointment.status === 'M'))"
                                label="Invoice" icon="pi pi-check"
                                :loading="submitted" @click="callGenerateInvoice()"
                                :disabled="!appointment.type">
                            </Button>

                            <Button v-if="view === 'invoice' && (this.userConfiguration.enableInvoicing !== 'N')"
                                    label="Submit" icon="pi pi-check"
                                    :loading="submitted"
                                    :disabled="submitted || !appointment.type"
                                    @click="generateInvoice()">
                            </Button>

                            <Button v-if="(view === 'display') && (this.appointment.status === 'I')"
                                    label="View" icon="pi pi-check"
                                    :loading="submitted" @click="viewInvoice()" :disabled="!appointment.type"></Button>
                        </template>
                    </Dialog>

                    <div class="legend">
                        <h3>Calendar Color Legend</h3>
                        <ul>
                            <li><span class="color-box blue"></span>Scheduled</li>
                            <li><span class="color-box purple"></span>Completed</li>
                            <li><span class="color-box red"></span>Missed</li>
                            <li><span class="color-box orange"></span>Invoiced</li>
                            <li><span class="color-box green"></span>Paid</li>
                            <li><span class="color-box magenta"></span>Pending</li>
                            <li><span class="color-box default1"></span>Personal</li>
                            <li><span class="color-box default2"></span>Booking</li>
                        </ul>
                    </div>

                </div>

            </div>
        </div>
    </div>

    <IsoEvolveFooter/>

</template>

<script>
import IsoEvolveFooter from "@/components/heading/authentication/IsoEvolveFooter.vue";
import MenuHealthCompact from "@/components/menu/health/MenuHealthCompact.vue";
import {useUserStore} from "@/stores/UserStore";
import UserService from "@/service/UserService";
import CompanyService from "@/service/CompanyService";
import AppointmentService from "@/service/AppointmentService";
import numeral from "numeral";
import FullCalendar from '@fullcalendar/vue3';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import PractitionerService from "@/service/PractitionerService";
import BillingPlanService from "@/service/BillingPlanService";
import ClientService from "@/service/ClientService";
import LocationService from "@/service/LocationService";
import moment from 'moment-timezone';
import InvoiceService from "@/service/InvoiceService";
import ProgressSpinner from "primevue/progressspinner";
import ClientProvisionComponentService from "@/service/ClientProvisionComponentService";
import BankAccountService from "@/service/BankAccountService";
import MenuFinancialCompact from "@/components/menu/famodule/MenuFinancialCompact.vue";

export default {
    components: {
        MenuFinancialCompact,
        ProgressSpinner,
        IsoEvolveFooter, MenuHealthCompact, FullCalendar
    },
    setup() {
        const userStore = useUserStore()

        return {
            userStore
        }
    },
    computed: {
        parsedScheduled: {
            get() {
                return new Date(this.appointment.scheduled);
            },
            set(value) {
                this.appointment.scheduled = value instanceof Date ? value.toISOString() : '';
            }
        },
        parsedInvoiced: {
            get() {
                return new Date(this.invoiceOptions.invoiced);
            },
            set(value) {
                this.invoiceOptions.invoiced = value instanceof Date ? value.toISOString() : '';
            }
        },
        canCreateAppointment() {
            if (this.newAppointment.type === 'P') {
                if ((this.newAppointment.description) && (this.newAppointment.scheduled) &&
                    (this.newAppointment.status)) {
                    return true;
                } else {
                    return false;
                }
            } else if ((this.newAppointment.type === 'B') || (this.newAppointment.type === 'N')) {
                if ((this.newAppointment.client) && (this.newAppointment.scheduled) &&
                    (this.newAppointment.status) && (this.newAppointment.duration)) {
                    return true;
                } else {
                    return false;
                }
            } else {
                return true;
            }
        },

    },
    watch: {
        'userStore.activeCompanyId': {
            immediate: false,
            async handler(newCompanyId, oldCompanyId) {
                if (!newCompanyId || newCompanyId === oldCompanyId) {
                    return;
                }

                console.log(
                    '[Invoices][watch] Company changed:',
                    oldCompanyId,
                    '→',
                    newCompanyId
                );

                await this.loadCompanyContext();
            }
        }
    },
    async mounted() {
        await this.loadCompanyContext();
    },
    data() {
        return {
            calendarOptions: {
                plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
                initialView: 'dayGridMonth',
                initialDate: new Date().toISOString().split('T')[0],
                headerToolbar: {
                    left: 'prev,next today',
                    center: 'title',
                    right: 'dayGridMonth,timeGridWeek,timeGridDay'
                },
                events: [], // leave as empty for now
                datesSet: null,
                height: 800,
                editable: true,
                selectable: true,
                eventClick: null,
                dateClick: null
            },
            profile: {},
            submitted: false,
            selectedPractitioner: null,
            company: {},
            companyConfiguration: {},
            userConfiguration: {},
            appointment: {},
            appointments: [],
            originalAppointments: null,
            newAppointment: {},
            practitioners: [],
            displayPractitioners: [],
            locations: [],
            billingPlans: [],
            showInvoiceViewer: false,
            appointmentId: null,
            actionId: null,
            invoiceTypes: [
                {name: 'Individual', code: 'I'},
                {name: 'Consolidated', code: 'C'}
            ],
            statusTypes: [
                {name: 'Pending', code: 'E'},
                {name: 'Scheduled', code: 'S'},
                // {name: 'Invoiced', code: 'I'},
                {name: 'Missed', code: 'M'},
                {name: 'Completed', code: 'C'},
                {name: 'Booking', code: 'B'},
                // {name: 'Paid', code: 'P'}
            ],
            confirmationTypes: [
                {name: 'Yes', code: 'Y'},
                {name: 'No', code: 'N'}
            ],
            rateOptionTypes: [
                {name: 'Base Rate (Default)', code: 'D'},
                {name: 'Premier Rate A', code: 'A'},
                {name: 'Premier Rate B', code: 'B'}
            ],
            appointmentTypes: [
                {name: 'Personal', code: 'P'},
                {name: 'Billable', code: 'B'},
                {name: 'Non-Billable', code: 'N'}
            ],
            frequencyTypes: [
                {name: 'None', code: 'N'},
                {name: 'Daily', code: 'D'},
                {name: 'Weekly', code: 'W'},
                {name: 'Bi-Weekly', code: 'B'},
                {name: 'Monthly', code: 'M'},
                {name: 'Annual', code: 'A'}
            ],
            paymentTypes: [
                {name: 'Cash', code: 'C'},
                {name: 'Bank Transfer', code: 'B'},
                {name: 'Credit Card', code: 'C'},
                {name: 'Healthcare Insurance', code: 'H'},
                {name: 'Cryptocurrency', code: 'R'},
                {name: 'Other', code: 'O'}
            ],
            submissionMethod: [
                {name: 'Invoice Only', code: 'C'},
                {name: 'Invoice and Health Insurance Claim', code: 'B'}
            ],
            healthInsuranceSubmissionMethod: [
                {name: 'Email', code: 'E'},
                {name: 'Electronic Data Interchange (EDI)', code: 'D'}
            ],
            filteredPractitioners: [],
            filteredBillingPlans: [],
            filteredClients: [],
            filteredLocations: [],
            filteredProvisionComponents: [],
            filteredBankAccounts: [],
            clientProvisionComponents: [],
            bankAccounts: [],
            showDialog: false,
            view: "display",
            changedEvent: {},
            invoiceOptions: {},
        }
    },
    methods: {
        async loadCompanyContext() {
            await this.initUserAndCompanyData(); // just gets this.profile set
            await this.initCalendar();
            await this.initSupportingData();
        },
        async initUserAndCompanyData() {
            try {
                // Attempt to get user data
                const userData = await UserService.getUser(this.userStore.user.id);
                this.profile = userData.data;

                this.profile.companyId = this.userStore.activeCompanyId;

                const companyData = await CompanyService.getCompany(this.profile.companyId);
                this.company = companyData.data;

            } catch (e) {
                console.error("[AppointmentsNew][mounted] Error fetching user data = ", e);
                this.userStore.clearUser();
                this.$router.push('login');
                return;
            }
        },
        async initCalendar() {
            this.calendarOptions.events = this.loadAppointmentsLazy.bind(this);
            this.calendarOptions.datesSet = this.onCalendarDatesSet;
            this.calendarOptions.eventClick = this.onEventClick;
            this.calendarOptions.dateClick = this.onDateSelect;

            await this.$nextTick(() => {
                const calendarApi = this.$refs.calendarRef?.getApi();
                if (calendarApi) {
                    calendarApi.refetchEvents();
                }
            });
        },
        async initSupportingData() {
            const [userConfig, companyConfig] = await Promise.all([
                UserService.getUserConfig(this.userStore.user.id),
                CompanyService.getCompanyConfiguration(this.profile.companyId),
            ]);
            this.userConfiguration = userConfig.data;
            this.companyConfiguration = companyConfig.data;

            const [practitioners, locations, billingPlans, bankAccountData] = await Promise.all([
                PractitionerService.getCompanyPractitioners(this.profile.companyId),
                LocationService.getCompanyLocations(this.profile.companyId),
                BillingPlanService.getActiveBillingPlans(this.profile.companyId),
                BankAccountService.getBankAccountsForCompany(this.profile.companyId),
            ]);

            this.userStore.setPractitioners(practitioners.data);
            this.userStore.setLocations(locations.data);
            this.userStore.setBillingPlans(billingPlans.data);

            this.practitioners = practitioners.data;

            this.locations = locations.data;
            this.billingPlans = billingPlans.data;
            this.bankAccounts = bankAccountData.data;

            console.log("[Calendar][mounted] this.bankAccounts = ", this.bankAccounts);

            if (this.userStore.user.rights === 'M') {
                const practitioner = this.practitioners.find(
                    p => p.userId === this.userStore.user.id
                );

                if (practitioner) {
                    this.selectedPractitioner = practitioner.id;
                }
            } else {
                this.displayPractitioners = [...this.practitioners];

                if ((this.displayPractitioners.length > 0) && (this.selectedPractitioner === null)) {
                    const practitionerEntry = {
                        id: 0,
                        fullName: "All"
                    };

                    this.displayPractitioners.unshift(practitionerEntry);

                    this.selectedPractitioner = this.displayPractitioners[0].id;
                } else if (this.displayPractitioners.length > 0) {
                    console.log("[Calendar][mounted] this.displayPractitioners = ", this.displayPractitioners);
                    const practitionerEntry = {
                        id: 0,
                        fullName: "All"
                    };

                    this.displayPractitioners.unshift(practitionerEntry);
                }
            }

            console.log("[Calendar][mounted] this.practitioners = ", this.practitioners);
            console.log("[Calendar][mounted] this.displayPractitioners = ", this.displayPractitioners);
            console.log("[Calendar][mounted] this.practitioners.length = ", this.practitioners.length);
            console.log("[Calendar][mounted] this.selectedPractitioner = ", this.selectedPractitioner);
            console.log("[Calendar][mounted] this.userStore.user.rights = ", this.userStore.user.rights);
        },
        loadAppointmentsLazy(fetchInfo, successCallback, failureCallback) {
            console.log('Lazy load triggered:', fetchInfo.startStr, 'to', fetchInfo.endStr);
            this.submitted = true;

            if (!this.profile.companyId) {
                console.warn('[Calendar] Company ID not available yet, skipping lazy load.');
                failureCallback(new Error('Company ID is not defined.'));
                this.submitted = false;
                return;
            }

            const statusColorMap = {
                'S': '#4495f1',      // Scheduled
                'C': '#cc8aba',    // Completed
                'M': 'red',       // Missed
                'I': 'orange',    // Invoiced
                'P': '#78ce74',     // Paid
                'E': 'magenta',   // Pending
                'A': '#A42BEAFF', // Personal
                'B': '#74e3c5',   // Booking
                'default': '#55c2da'
            };

            console.log('[Calendar] this.userStore.user.rights = ', this.userStore.user.rights);
            console.log('[Calendar] this.userStore.user.practitionerId = ', this.userStore.user.practitionerId);

            if (this.userStore.user.rights === 'M') {
                AppointmentService.getPractitionerAppointmentsLazyLoading(
                    this.userStore.user.practitionerId,
                    {first: 0, rows: 100000},
                    '', '', fetchInfo.startStr, fetchInfo.endStr
                ).then(response => {
                    const appointments = response.data.content || [];

                    const events = appointments.map(item => {
                        const start = new Date(item.scheduled);
                        const end = new Date(start.getTime() + item.duration * 60000);

                        let tempStatus = item.status;
                        let eventTitle = item.description || '';

                        // Add label to title
                        if (item.type === 'P') {
                            eventTitle += ' [P]';
                            item.status = 'A';
                        } else if (item.type === 'N') {
                            eventTitle += ' [N]';
                        } else {
                            eventTitle += ' [B]';
                        }

                        const backgroundColor = statusColorMap[item.status] || statusColorMap['default'];

                        // Restore original status if modified
                        if (item.type === 'P') {
                            item.status = tempStatus;
                        }

                        return {
                            title: eventTitle,
                            start: start.toISOString(),
                            end: end.toISOString(),
                            backgroundColor: backgroundColor,
                            textColor: 'black',
                            extendedProps: {appointmentId: item.id}
                        };
                    });

                    successCallback(events);
                    this.submitted = false;
                }).catch(error => {
                    console.error('[Calendar] Failed to fetch events:', error);
                    failureCallback(new Error(error?.message || 'Lazy load error'));
                    this.submitted = false;
                });
            } else {
                if ((this.practitioners.length === 0) || (this.selectedPractitioner === null) || (this.selectedPractitioner === 0)) {
                    AppointmentService.getCompanyAppointmentsLazyLoading(
                        this.profile.companyId,
                        {first: 0, rows: 100000},
                        '', '', fetchInfo.startStr, fetchInfo.endStr
                    ).then(response => {
                        const appointments = response.data.content || [];

                        const events = appointments.map(item => {
                            const start = new Date(item.scheduled);
                            const end = new Date(start.getTime() + item.duration * 60000);

                            let tempStatus = item.status;
                            let eventTitle = item.description || '';

                            // Add label to title
                            if (item.type === 'P') {
                                eventTitle += ' [P]';
                                item.status = 'A';
                            } else if (item.type === 'N') {
                                eventTitle += ' [N]';
                            } else {
                                eventTitle += ' [B]';
                            }

                            const backgroundColor = statusColorMap[item.status] || statusColorMap['default'];

                            // Restore original status if modified
                            if (item.type === 'P') {
                                item.status = tempStatus;
                            }

                            return {
                                title: eventTitle,
                                start: start.toISOString(),
                                end: end.toISOString(),
                                backgroundColor: backgroundColor,
                                textColor: 'black',
                                extendedProps: {appointmentId: item.id}
                            };
                        });

                        successCallback(events);
                        this.submitted = false;
                    }).catch(error => {
                        console.error('[Calendar] Failed to fetch events:', error);
                        failureCallback(new Error(error?.message || 'Lazy load error'));
                        this.submitted = false;
                    });

                } else {
                    AppointmentService.getPractitionerAppointmentsLazyLoading(
                        this.selectedPractitioner,
                        {first: 0, rows: 100000},
                        '', '', fetchInfo.startStr, fetchInfo.endStr
                    ).then(response => {
                        const appointments = response.data.content || [];

                        const events = appointments.map(item => {
                            const start = new Date(item.scheduled);
                            const end = new Date(start.getTime() + item.duration * 60000);

                            let tempStatus = item.status;
                            let eventTitle = item.description || '';

                            // Add label to title
                            if (item.type === 'P') {
                                eventTitle += ' [P]';
                                item.status = 'A';
                            } else if (item.type === 'N') {
                                eventTitle += ' [N]';
                            } else {
                                eventTitle += ' [B]';
                            }

                            const backgroundColor = statusColorMap[item.status] || statusColorMap['default'];

                            // Restore original status if modified
                            if (item.type === 'P') {
                                item.status = tempStatus;
                            }

                            return {
                                title: eventTitle,
                                start: start.toISOString(),
                                end: end.toISOString(),
                                backgroundColor: backgroundColor,
                                textColor: 'black',
                                extendedProps: {appointmentId: item.id}
                            };
                        });

                        successCallback(events);
                        this.submitted = false;
                    }).catch(error => {
                        console.error('[Calendar] Failed to fetch events:', error);
                        failureCallback(new Error(error?.message || 'Lazy load error'));
                        this.submitted = false;
                    });
                }
            }

            this.submitted = false;
        },
        onCalendarDatesSet({start, end}) {
            console.log('View changed:', start, end);
        },
        formatAmount(value) {
            if (value) {
                return numeral(String(value)).format('0,0.00');
            } else {
                return 0;
            }
        },
        getFilteredItems(data) {
            console.log("[Calendar][getFilteredItems] data = ", data);

            const items = [];

            items.push({
                label: 'Invoice',
                class: 'mb-4 mr-4 ml-4 mt-4',
                command: () => {
                    this.callAllocatePayment(data);
                }
            });

            console.log('[getFilteredItems] items = ', items);

            return items;
        },
        chooseBlankSelection(data) {
            console.log('[Calendar][chooseBlankSelection] data = ', data);
            this.$toast.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Please select a valid option.',
                life: 3000
            });
        },
        async onTogglePractitioner() {
            console.log("[Calendar][onTogglePractitioner] this.selectedPractitioner = ", this.selectedPractitioner);

            await this.initCalendar();
            await this.initSupportingData();
        },
        async callGenerateInvoice() {
            console.log("[Calendar][callGenerateInvoice] this.appointment = ", this.appointment);

            this.showDialog = true;
            this.invoiceOptions.autoEmailAndClaim = 'N';
            this.invoiceOptions.autoAllocatePayment = 'N';
            this.invoiceOptions.invoiced = this.appointment.scheduled;
            this.invoiceOptions.type = 'I';

            if (this.companyConfiguration.type !== 'H') {
                this.invoiceOptions.submissionMethod = 'C';
                this.invoiceOptions.healthInsuranceSubmission = 'E';
                this.invoiceOptions.useSystemProcedureRates = 'N';
                this.invoiceOptions.useSystemProductRates = 'N';
            } else {
                this.invoiceOptions.submissionMethod = 'B';
                this.invoiceOptions.healthInsuranceSubmission = 'D';
                this.invoiceOptions.useSystemProcedureRates = 'N';
                this.invoiceOptions.rateOption = 'D';
            }

            this.invoiceOptions.useAutomaticSubmission = 'N';
            this.invoiceOptions.useTaxRate = this.company.taxRegistered;

            const startDate = moment().startOf('month');
            const endDate = moment(this.appointment.scheduled)
                .set({hour: 23, minute: 59, second: 0, millisecond: 0});

            this.invoiceOptions.startDate = startDate.format('YYYY-MM-DD HH:mm');
            this.invoiceOptions.endDate = endDate.format('YYYY-MM-DD HH:mm');

            console.log("[Calendar][callGenerateInvoice] this.invoiceOptions = ", this.invoiceOptions);

            try {
                const [billingPlanData] = await Promise.all([
                    BillingPlanService.getBillingPlan(this.appointment.billingPlanId)
                ]);

                this.billingPlan = billingPlanData.data;
                console.log("[Calendar][callGenerateInvoice] billingPlanData.data = ", billingPlanData.data);

                this.invoiceOptions.useSystemProcedureRates = this.billingPlan.useSystemProcedureRates;
                this.invoiceOptions.useSystemProductRates = this.billingPlan.useSystemProductRates;
                this.invoiceOptions.healthInsuranceSubmission = this.billingPlan.healthInsuranceSubmission;
                this.invoiceOptions.bankAccount = this.bankAccounts && this.bankAccounts.length > 0 ? this.bankAccounts[0] : null;

                console.log("[Calendar][callGenerateInvoice] this.invoiceOptions = ", this.invoiceOptions);

            } catch (e) {
                console.log("[Calendar][callGenerateInvoice] Could not get billing plan = ", this.appointment.billingPlanId);
            }

            if (this.appointment.clientMedicalName === null) {
                this.invoiceOptions.useSystemProcedureRates = 'N';
                this.invoiceOptions.submissionMethod = 'C';
            }

            this.view = "invoice";

        },
        async callDeleteAppointment() {
            console.log("[Calendar][callDeleteAppointment] this.appointment = ", this.appointment);

            this.showDialog = true;

            this.view = "delete";

        },
        async onEventClick(e) {

            console.log("[Calendar][onEventClick] Got here. e = ", e);

            console.log("[Calendar][onEventClick] Got here. this.changedEvent = ", this.changedEvent);

            const appointmentId = e.event.extendedProps.appointmentId;

            console.log("[Calendar][onEventClick] appointmentId = ", appointmentId);

            console.log("[Calendar][onEventClick] Got here. e.title = ", e.event.title);
            console.log("[Calendar][onEventClick] Got here. e.start = ", e.event.start);
            console.log("[Calendar][onEventClick] Got here. e.end = ", e.event.end);

            this.changedEvent.title = e.event.title;
            this.changedEvent.start = e.event.start;
            this.changedEvent.end = e.event.end;

            const appointmentsResponse = await AppointmentService.getAppointment(appointmentId);
            console.log('[Calendar][onEventClick] Ge appointments response = ', appointmentsResponse);

            this.appointment = appointmentsResponse.data;
            console.log('[Calendar][onEventClick] this.appointment = ', this.appointment);

            try {
                const response = await ClientProvisionComponentService
                    .getClientProvisionComponentsForClient(this.appointment.clientId);

                this.clientProvisionComponents = response.data ?? [];
                console.log("[Calendar][onEventClick] Loaded provision components:", this.clientProvisionComponents);
            } catch (e) {
                console.log("[Calendar][onEventClick] Error loading provision components:", e);
                this.clientProvisionComponents = [];
            }

            this.showDialog = true;

            if ((this.appointment.status === 'I') || (this.appointment.status === 'P')) {
                this.view = "display";
            } else {
                this.view = "edit";
            }

        },
        onDateSelect(e) {
            console.log("[Calendar][onDateSelect] Got here. e = ", e);

            const selectedDate = e.date || e.start;

            console.log("[Calendar][onDateSelect] Selected Date: ", selectedDate);

            this.showDialog = true;
            this.view = "new";

            this.newAppointment = {
                type: 'B',
                duration: 60,
                frequency: 'N',
                frequencyLength: 0,
                scheduled: selectedDate,
                status: 'E',
                sendCalendarInvites: 'N',
                sendReminders: 'N',
                sendCreationReminder: 'N',
                sendMissedNotice: 'N',
                setupOnlineMeeting: 'N',
                client: null,
                practitioner: this.practitioners && this.practitioners.length > 0 ? this.practitioners[0] : null,
                location: this.locations && this.locations.length > 0 ? this.locations[0] : null,
                billingPlan: this.billingPlans && this.billingPlans.length > 0 ? this.billingPlans[0] : null
            };
        },
        async onEventDrop(e) {
            console.log("[Calendar][onEventDrop] Got here. e = ", e);

            this.changedEvent = {
                title: e.event.title,
                start: e.event.start,
                end: e.event.end
            };

            console.log("[Calendar][onEventClick] Got here. e = ", e);

            console.log("[Calendar][onEventClick] Got here. this.changedEvent = ", this.changedEvent);

            const appointmentId = e.event.extendedProps.appointmentId;

            console.log("[Calendar][onEventClick] appointmentId = ", appointmentId);

            console.log("[Calendar][onEventClick] Got here. e.title = ", e.event.title);
            console.log("[Calendar][onEventClick] Got here. e.start = ", e.event.start);
            console.log("[Calendar][onEventClick] Got here. e.end = ", e.event.end);

            this.changedEvent.title = e.event.title;
            this.changedEvent.start = e.event.start;
            this.changedEvent.end = e.event.end;

            const appointmentsResponse = await AppointmentService.getAppointment(appointmentId);
            console.log('[Appointments][onEventClick] Ge appointments response = ', appointmentsResponse);

            this.appointment = appointmentsResponse.data;
            console.log('[Appointments][onEventClick] this.appointment = ', this.appointment);

            this.appointment.scheduled = this.changedEvent.start;

            this.showDialog = true;

            if (this.appointment.status === 'I') {
                this.view = "display";
            } else {
                this.view = "edit";
            }

            this.showDialog = true;
            this.view = "edit";
        },
        onEventResize(e) {
            console.log("[Calendar][onEventResize] Got here. e = ", e);

            this.changedEvent = {
                title: e.event.title,
                start: e.event.start,
                end: e.event.end
            };

            this.showDialog = true;
            this.view = "edit";
        },
        saveEvent() {
            console.log("[Calendar][saveEvent] Got here. this.changedEvent = ", this.changedEvent);
            this.showDialog = false;
            this.view = "";
        },
        searchPractitioners(event) {
            console.log("[Calendar][searchPractitioners] event.query = ", event.query);

            const q = (event?.query ?? '').trim().toLowerCase();

            // If dropdown was clicked with an empty input, show all (or a cached subset)
            if (!q) {
                // Option A: use an already-fetched list
                this.filteredPractitioners = (this.practitioners ?? []).slice(0, 50); // cap if large
                return;
            }

            PractitionerService.searchCompanyPractitioners(this.profile.companyId, q)
                .then(data => {
                    console.log("[Calendar][searchPractitioners] Getting practitioner data for company = ", this.profile.companyId);
                    this.filteredPractitioners = data.data;
                    console.log("[Calendar][searchPractitioners] Got practitioner data = ", data.data);
                })
                .catch(e => {
                    console.log("[Calendar][searchPractitioners] Could not get practitioner data for company = ", this.profile.companyId);
                    console.log("[Calendar][searchPractitioners] Error = ", e);
                    this.filteredPractitioners = [];
                });

        },
        searchBankAccounts(event) {
            console.log("[Calendar][searchBankAccounts] event.query = ", event.query);

            const q = (event?.query ?? '').trim().toLowerCase();

            // If dropdown was clicked with an empty input, show all (or a cached subset)
            if (!q) {
                // Option A: use an already-fetched list
                this.filteredBankAccounts = (this.bankAccounts ?? []).slice(0, 50); // cap if large
                return;
            }

            BankAccountService.searchCompanyBankAccounts(this.profile.companyId, q)
                .then(data => {
                    console.log("[Calendar][searchBankAccounts] Getting bank account data for company = ",
                        this.profile.companyId);
                    this.filteredBankAccounts = data.data;
                    console.log("[Calendar][searchBankAccounts] Got bank account data = ", data.data);
                })
                .catch(e => {
                    console.log("[Calendar][searchBankAccounts] Could not get bank account data for company = ",
                        this.profile.companyId);
                    console.log("[Calendar][searchBankAccounts] Error = ", e);
                    this.filteredBankAccounts = [];
                });

        },
        searchBillingPlan(event) {
            console.log("[Calendar][searchBillingPlan] event.query = ", event.query);
            console.log("[Calendar][searchBillingPlan] this.filteredBillingPlans = ", this.filteredBillingPlans);
            console.log("[Calendar][searchBillingPlan] Getting billing plan data for company = ", this.profile.companyId);

            if (event.query === '') {
                console.log("[Calendar][searchBillingPlan] event.query is blank. So calling getActiveBillingPlans.");
                BillingPlanService.getActiveBillingPlans(this.profile.companyId)
                    .then(data => {
                        this.filteredBillingPlans = data.data;
                        console.log("[Calendar][searchBillingPlan] Got billing plan data = ", data.data);
                    })
                    .catch(e => {
                        console.log("[Calendar][searchBillingPlan] Could not get billing plan data for user = ", this.profile.companyId);
                        console.log("[Calendar][searchBillingPlan] Error = ", e);
                    });
            } else {
                console.log("[Calendar][searchBillingPlan] event.query is NOT blank. So calling searchBillingPlans.");
                BillingPlanService.searchBillingPlans(this.profile.companyId, event.query.toLowerCase())
                    .then(data => {
                        this.filteredBillingPlans = data.data;
                        console.log("[Calendar][searchBillingPlan] Got billing plan data = ", data.data);
                    })
                    .catch(e => {
                        console.log("[Calendar][searchBillingPlan] Could not get billing plan data for user = ", this.profile.companyId);
                        console.log("[Calendar][searchBillingPlan] Error = ", e);
                    });
            }

        },
        searchClients(event) {
            console.log("[Calendar][searchClients] event.query = ", event.query);

            ClientService.searchCompanyClientsActive(this.profile.companyId, event.query.toLowerCase())
                .then(data => {
                    console.log("[Calendar][searchClients] Getting client data for company = ", this.profile.companyId);
                    this.filteredClients = data.data;
                    console.log("[Calendar][searchClients] Got client data = ", data.data);
                })
                .catch(e => {
                    console.log("[Calendar][searchClients] Could not get client data for user = ", this.profile.companyId);
                    console.log("[Calendar][searchClients] Error = ", e);
                });

        },
        searchLocations(event) {
            console.log("[Calendar][searchLocations] event.query = ", event.query);

            const q = (event?.query ?? '').trim().toLowerCase();

            if (!q) {
                // Option A: use an already-fetched list
                this.filteredLocations = (this.locations ?? []).slice(0, 50); // cap if large
                return;
            }

            LocationService.searchCompanyLocations(this.profile.companyId, event.query.toLowerCase())
                .then(data => {
                    console.log("[Calendar][searchLocations] Getting location data for company = ", this.profile.companyId);
                    this.filteredLocations = data.data;
                    console.log("[Calendar][searchLocations] Got location data = ", data.data);
                })
                .catch(e => {
                    this.filteredLocations = [];

                    console.log("[Calendar][searchLocations] Could not get location data for user = ", this.profile.companyId);
                    console.log("[Calendar][searchLocations] Error = ", e);
                });
        },
        async onClientSelected(event) {
            const client = event.value;
            console.log("[Calendar][onClientSelected] Client selected:", client);

            this.clientProvisionComponents = [];

            if (client?.id) {
                try {
                    const response = await ClientProvisionComponentService
                        .getClientProvisionComponentsForClient(client.id);

                    this.clientProvisionComponents = response.data ?? [];
                    console.log("[Calendar][onClientSelected] Loaded provision components:", this.clientProvisionComponents);
                } catch (e) {
                    console.log("[Calendar][onClientSelected] Error loading provision components:", e);
                    this.clientProvisionComponents = [];
                }
            }

            // Reset provision component selection if no data
            if (this.clientProvisionComponents.length === 0) {
                this.newAppointment.provisionComponent = null;
            }
        },
        async searchProvisionComponentsOnCreateAppointment(event) {
            const q = (event?.query ?? "").trim().toLowerCase();

            console.log("[searchProvisionComponentsOnCreateAppointment] q =", q);

            if (!this.clientProvisionComponents || this.clientProvisionComponents.length === 0) {
                try {
                    const response = await ClientProvisionComponentService
                        .getClientProvisionComponentsForClient(this.newAppointment.client.id);

                    this.clientProvisionComponents = response.data ?? [];

                    console.log("[searchProvisionComponentsOnCreateAppointment] Loaded provisions:", this.clientProvisionComponents);
                } catch (e) {
                    console.log("[searchProvisionComponentsOnCreateAppointment] Error loading provisions:", e);
                    this.clientProvisionComponents = [];
                }
            }

            if (!q) {
                this.filteredProvisionComponents = this.clientProvisionComponents.slice(0, 50);
                return;
            }

            this.filteredProvisionComponents = this.clientProvisionComponents
                .filter(pc => pc.fullName?.toLowerCase().includes(q))
                .slice(0, 50);

            console.log("[searchProvisionComponentsOnCreateAppointment] Filtered:", this.filteredProvisionComponents);
        },
        async searchProvisionComponentsOnUpdateAppointment(event) {
            const q = (event?.query ?? "").trim().toLowerCase();

            console.log("[searchProvisionComponentsOnCreateAppointment] q =", q);

            if (!this.clientProvisionComponents || this.clientProvisionComponents.length === 0) {
                try {
                    const response = await ClientProvisionComponentService
                        .getClientProvisionComponentsForClient(this.appointment.clientId);

                    this.clientProvisionComponents = response.data ?? [];

                    console.log("[searchProvisionComponentsOnCreateAppointment] Loaded provisions:", this.clientProvisionComponents);
                } catch (e) {
                    console.log("[searchProvisionComponentsOnCreateAppointment] Error loading provisions:", e);
                    this.clientProvisionComponents = [];
                }
            }

            if (!q) {
                this.filteredProvisionComponents = this.clientProvisionComponents.slice(0, 50);
                return;
            }

            this.filteredProvisionComponents = this.clientProvisionComponents
                .filter(pc => pc.fullName?.toLowerCase().includes(q))
                .slice(0, 50);

            console.log("[searchProvisionComponentsOnCreateAppointment] Filtered:", this.filteredProvisionComponents);
        },
        async refreshAppointmentData() {

            this.submitted = true;

            const appointmentsResponse = await AppointmentService.getCompanyAppointments(this.profile.companyId);
            console.log('[Appointments][deleteAppointment] Ge appointments response = ', appointmentsResponse);

            this.appointments = appointmentsResponse.data;
            this.userStore.setAppointments(appointmentsResponse.data);

            const statusColorMap = {
                'S': '#4495f1',     // Scheduled
                'C': '#cc8aba',  // Completed
                'M': 'red',      // Missed
                'I': 'orange',   // Invoiced
                'P': '#78ce74',    // Paid
                'E': 'magenta',    // Pending
                'A': '#A42BEAFF',    // Personal
                'B': '#74e3c5',    // Booking
                'default': '#55c2da' // Default color
            };

            this.calendarOptions.events = [];

            this.appointments.forEach(item => {
                    const startDate = new Date(item.scheduled);
                    const startDateTime = startDate.toISOString();

                    const endDate = new Date(item.scheduled);
                    endDate.setMinutes(endDate.getMinutes() + item.duration);
                    const endDateTime = endDate.toISOString();

                    var tempStatus = item.status;

                    this.eventDescription = item.description;
                    if (item.type === 'P') {
                        this.eventDescription = this.eventDescription + " [P]";
                        item.status = 'A';

                        console.log("[Calendar][saveNewAppointment] Item = ", item);
                        console.log("[Calendar][saveNewAppointment] tempStatus = ", tempStatus);

                    } else if (item.type === 'N') {
                        this.eventDescription = this.eventDescription + " [N]";
                    } else {
                        this.eventDescription = this.eventDescription + " [B]";
                    }

                    // Map status to class name
                    const buttonColor = statusColorMap[item.status] || statusColorMap['default'];

                    if (item.type === 'P') {
                        item.status = tempStatus;
                    }

                    const newEvent = {
                        title: this.eventDescription,
                        start: startDateTime,
                        end: endDateTime,
                        backgroundColor: buttonColor, // Use backgroundColor for FullCalendar to set inline style
                        textColor: 'black',
                        extendedProps: {
                            appointmentId: item.id // Add appointment ID here
                        }
                    };

                    this.calendarOptions.events.push(newEvent);

                }
            )
            ;

            console.log("[" + name + "][saveNewAppointment] this.calendarOptions.events = ", this.calendarOptions.events);

            this.submitted = false;
        },
        getTimezone(region) {
            switch ((region || "").toUpperCase()) {
                case "ZA":
                    return "Africa/Johannesburg";
                case "UK":
                case "GB":
                    return "Europe/London";
                case "US":
                case "USA":
                case "EST":
                    return "America/New_York";
                case "CA":
                case "CANADA":
                    return "America/Toronto";
                case "IN":
                case "INDIA":
                    return "Asia/Kolkata";
                case "NA":
                case "NAMIBIA":
                    return "Africa/Windhoek";
                case "BW":
                case "BOTSWANA":
                    return "Africa/Gaborone";
                default:
                    return "Europe/London";
            }
        },
        async createAppointment() {
            console.log("[" + name + "][deleteAppointment] Start.");

            this.submitted = true;

            if (this.newAppointment.type) {

                try {
                    console.log("[Calendar][saveAppointment] this.newAppointment.client = ", this.newAppointment.client);
                    console.log("[Calendar][saveAppointment] this.newAppointment.practitioner = ", this.newAppointment.practitioner);
                    console.log("[Calendar][saveAppointment] this.newAppointment.billingPlan = ", this.newAppointment.billingPlan);
                    console.log("[Calendar][saveAppointment] this.newAppointment.location = ", this.newAppointment.location);
                    console.log("[Calendar][saveAppointment] this.newAppointment.provisionComponent = ", this.newAppointment.provisionComponent);

                    var tempDescription = this.newAppointment.description;
                    var tempClient = "";
                    var tempPractitioner = "";
                    var tempBillingPlan = "";
                    var tempLocation = "";
                    var tempProvisionComponent = "";

                    if (this.newAppointment.type !== 'P') {

                        try {
                            tempClient = this.newAppointment.client.id;
                            tempDescription = this.newAppointment.client.name;
                        } catch (error) {
                            tempClient = "";
                        }

                        try {
                            tempPractitioner = this.newAppointment.practitioner.id;
                        } catch (error) {
                            tempPractitioner = "";
                        }

                        try {
                            tempBillingPlan = this.newAppointment.billingPlan.id;
                        } catch (error) {
                            tempBillingPlan = "";
                        }

                        try {
                            tempLocation = this.newAppointment.location.id;
                        } catch (error) {
                            tempLocation = "";
                        }

                        try {
                            tempProvisionComponent = this.newAppointment.provisionComponent.id;
                        } catch (error) {
                            tempProvisionComponent = "";
                        }
                    }

                    var tz = this.getTimezone(this.companyConfiguration?.region) || "Europe/London";
                    const m = moment(this.newAppointment.scheduled);

                    console.log("[Calendar][createAppointment] tz = ", tz);

                    var newAppointmentDto = {
                        type: this.newAppointment.type,
                        clientId: tempClient,
                        practitionerId: tempPractitioner,
                        billingPlanId: tempBillingPlan,
                        locationId: tempLocation,
                        clientProvisionComponentId: tempProvisionComponent,
                        description: tempDescription,
                        frequency: this.newAppointment.frequency,
                        frequencyLength: this.newAppointment.frequencyLength,
                        duration: this.newAppointment.duration,
                        status: this.newAppointment.status,
                        scheduled: m.format('YYYY-MM-DD HH:mm'),
                        utcScheduled: moment.tz(m.format('YYYY-MM-DD HH:mm'), tz).toISOString(),
                        sendCalendarInvites: this.newAppointment.sendCalendarInvites,
                        sendReminders: this.newAppointment.sendReminders,
                        sendCreationReminder: this.newAppointment.sendCreationReminder,
                        sendMissedNotice: this.newAppointment.sendMissedNotice,
                        setupOnlineMeeting: this.newAppointment.setupOnlineMeeting,
                        provision: null,
                        requireNote: 'N'
                    };

                    console.log("[AppointmentsNew][saveAppointment] Before creating appointment back-end call. Data = ", newAppointmentDto);

                    const response = await AppointmentService.createAppointment(this.profile.companyId, newAppointmentDto);
                    console.log('[Appointments][deleteAppointment] Delete response = ', response);

                    this.showDialog = false;

                    this.$refs.calendarRef?.getApi().refetchEvents();

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Created appointment',
                        life: 3000
                    });
                } catch (error) {
                    console.error('[Appointments][deleteAppointment] Error:', error);

                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to create appointment. Please try again.',
                        life: 3000
                    });
                }
            }

            this.newAppointment = {
                type: 'B',
                duration: 60,
                frequency: 'N',
                frequencyLength: 0,
                scheduled: moment().toDate(),
                status: 'E',
                sendCalendarInvites: 'N',
                sendReminders: 'N',
                sendCreationReminder: 'N',
                sendMissedNotice: 'N',
                setupOnlineMeeting: 'N',
                client: null,
                practitioner: this.practitioners && this.practitioners.length > 0 ? this.practitioners[0] : null,
                location: this.locations && this.locations.length > 0 ? this.locations[0] : null,
                billingPlan: this.billingPlans && this.billingPlans.length > 0 ? this.billingPlans[0] : null
            };

            this.showDialog = false;
            this.view = "display";
            this.submitted = false;
        }
        ,
        async updateAppointment() {
            console.log("[" + name + "][deleteAppointment] Start.");

            this.submitted = true;

            if (this.appointment.id) {

                try {
                    var tempClient = this.appointment.clientName;
                    var tempDescription = this.appointment.description;
                    var tempPractitioner = this.appointment.practitionerName;
                    var tempBillingPlan = this.appointment.billingPlanName;
                    var tempLocation = this.appointment.locationName;
                    var tempProvisionComponent = this.appointment.clientProvisionComponentName;

                    console.log("[Calendar][updateAppointment] BEFORE. tempDescription = ", tempDescription);
                    console.log("[Calendar][updateAppointment] BEFORE. tempClient = ", tempClient);
                    console.log("[Calendar][updateAppointment] BEFORE. tempPractitioner = ", tempPractitioner);
                    console.log("[Calendar][updateAppointment] BEFORE. tempBillingPlan = ", tempBillingPlan);
                    console.log("[Calendar][updateAppointment] BEFORE. tempLocation = ", tempLocation);
                    console.log("[Calendar][updateAppointment] BEFORE. tempProvisionComponent = ", tempProvisionComponent);

                    if (this.appointment.type !== 'P') {

                        try {
                            if (this.appointment.clientName.id == null) {
                                console.log("[" + name + "][updateAppointment] NULL CASE. BEFORE. tempClient = ", tempClient);
                                tempClient = this.appointment.clientId;
                                console.log("[" + name + "][updateAppointment] NULL CASE. AFTER. tempClient = ", tempClient);
                                tempDescription = this.appointment.description;
                                console.log("[" + name + "][updateAppointment] NULL CASE. AFTER. tempDescription = ", tempDescription);
                            } else {
                                console.log("[" + name + "][updateAppointment] INSIDE. BEFORE. tempClient = ", tempClient);
                                tempClient = this.appointment.clientName.id;
                                console.log("[" + name + "][updateAppointment] INSIDE. AFTER. tempClient = ", tempClient);
                                tempDescription = this.appointment.clientName.name;
                                console.log("[" + name + "][updateAppointment] INSIDE. AFTER. tempDescription = ", tempDescription);
                            }
                        } catch (error) {
                            console.log("[" + name + "][updateAppointment] EXCEPTION. BEFORE. tempClient = ", tempClient);
                            tempClient = this.appointment.clientId;
                            console.log("[" + name + "][updateAppointment] EXCEPTION. AFTER. tempClient = ", tempClient);
                            tempDescription = this.appointment.description;
                            console.log("[" + name + "][updateAppointment] EXCEPTION. AFTER. tempDescription = ", tempDescription);
                        }

                        try {
                            if (this.appointment.practitionerName.id == null) {
                                console.log("[" + name + "][updateAppointment] NULL CASE. BEFORE. tempPractitioner = ", tempPractitioner);
                                tempPractitioner = this.appointment.practitionerId;
                                console.log("[" + name + "][updateAppointment] NULL CASE. AFTER. tempPractitioner = ", tempPractitioner);
                            } else {
                                console.log("[" + name + "][updateAppointment] INSIDE. BEFORE. tempPractitioner = ", tempPractitioner);
                                tempPractitioner = this.appointment.practitionerName.id;
                                console.log("[" + name + "][updateAppointment] INSIDE. AFTER. tempPractitioner = ", tempPractitioner);
                            }
                        } catch (error) {
                            tempPractitioner = this.appointment.practitionerId;
                        }

                        try {
                            if (this.appointment.billingPlanName.id == null) {
                                tempBillingPlan = this.appointment.billingPlanId;
                            } else {
                                tempBillingPlan = this.appointment.billingPlanName.id;
                            }

                        } catch (error) {
                            tempBillingPlan = this.appointment.billingPlanId;
                        }

                        try {
                            if (this.appointment.locationName.id == null) {
                                tempLocation = this.appointment.locationId;
                            } else {
                                tempLocation = this.appointment.locationName.id;
                            }
                        } catch (error) {
                            tempLocation = this.appointment.locationId;
                        }

                        try {
                            if (!this.appointment.clientProvisionComponentName) {
                                console.log("[Calendar][updateAppointment] OUTSIDE. BEFORE. tempProvisionComponent = ", tempProvisionComponent);
                                tempProvisionComponent = null;
                                console.log("[Calendar][updateAppointment] OUTSIDE. AFTER. tempProvisionComponent = ", tempProvisionComponent);
                            } else {
                                if (this.appointment.clientProvisionComponentName.id == null) {
                                    console.log("[Calendar][updateAppointment] NULL CASE. BEFORE. tempProvisionComponent = ", tempProvisionComponent);
                                    tempProvisionComponent = this.appointment.clientProvisionComponentId;
                                    console.log("[Calendar][updateAppointment] NULL CASE. AFTER. tempProvisionComponent = ", tempProvisionComponent);
                                } else {
                                    console.log("[Calendar][updateAppointment] INSIDE. BEFORE. tempProvisionComponent = ", tempProvisionComponent);
                                    tempProvisionComponent = this.appointment.clientProvisionComponentName.id;
                                    console.log("[Calendar][updateAppointment] INSIDE. AFTER. tempProvisionComponent = ", tempProvisionComponent);
                                }
                            }
                        } catch (error) {
                            console.log("[Calendar][updateAppointment] CATCH. error = ", error);
                            tempProvisionComponent = this.appointment.clientProvisionComponentId;
                            console.log("[Calendar][updateAppointment] CATCH. tempProvisionComponent = ", tempProvisionComponent);
                        }

                    }

                    var tz = this.getTimezone(this.companyConfiguration?.region) || "Europe/London";
                    const m = moment(this.appointment.scheduled);

                    console.log("[Calendar][updateAppointment] tz = ", tz);

                    var newAppointmentDto = {
                        type: this.appointment.type,
                        clientId: tempClient,
                        practitionerId: tempPractitioner,
                        billingPlanId: tempBillingPlan,
                        locationId: tempLocation,
                        clientProvisionComponentId: tempProvisionComponent,
                        description: tempDescription,
                        frequency: this.appointment.frequency,
                        frequencyLength: this.appointment.frequencyLength,
                        duration: this.appointment.duration,
                        scheduled: m.format('YYYY-MM-DD HH:mm'),
                        utcScheduled: moment.tz(m.format('YYYY-MM-DD HH:mm'), tz).toISOString(),
                        status: this.appointment.status,
                        sendCalendarInvites: this.appointment.sendCalendarInvites,
                        sendReminders: this.appointment.sendReminders,
                        sendCreationReminder: this.appointment.sendCreationReminder,
                        sendMissedNotice: this.appointment.sendMissedNotice,
                        setupOnlineMeeting: this.appointment.setupOnlineMeeting,
                        sequenceCount: this.appointment.sequenceCount,
                        excludedDays: this.appointment.excludedDays
                    };

                    console.log("[Calendar][updateAppointment] Before creating appointment back-end call. Data = ", newAppointmentDto);

                    const response = await AppointmentService.updateAppointment(this.appointment.id, newAppointmentDto);
                    console.log('[Calendar][updateAppointment] Delete response = ', response);

                    this.showDialog = false;

                    // this.initCalendar();
                    this.$refs.calendarRef?.getApi().refetchEvents();

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Updated appointment',
                        life: 3000
                    });
                } catch (error) {
                    console.error('[Calendar][updateAppointment] Error:', error);

                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to update appointment. Please try again.',
                        life: 3000
                    });
                }
            }

            this.showDialog = false;
            this.view = "display";
            this.submitted = false;
        },
        async deleteAppointment() {
            console.log("[" + name + "][deleteAppointment] Start.");

            this.submitted = true;

            if (this.appointment.id) {

                try {

                    const response = await AppointmentService.deleteAppointment(this.appointment.id);
                    console.log('[Appointments][deleteAppointment] Delete response = ', response);

                    this.showDialog = false;

                    // this.initCalendar();
                    this.$refs.calendarRef?.getApi().refetchEvents();

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Deleted appointment',
                        life: 3000
                    });
                } catch (error) {
                    console.error('[Appointments][deleteAppointment] Error:', error);

                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to delete appointment. Please try again.',
                        life: 3000
                    });
                }
            }

            this.showDialog = false;
            this.view = "display";
            this.submitted = false;
        },
        async deleteAppointmentAll() {
            console.log("[" + name + "][deleteAppointmentAll] Start.");

            this.submitted = true;

            if (this.appointment.id) {

                try {

                    const response = await AppointmentService.deleteAppointmentAll(this.appointment.id);
                    console.log('[Appointments][deleteAppointmentAll] Delete response = ', response);

                    this.showDialog = false;

                    // this.initCalendar();
                    this.$refs.calendarRef?.getApi().refetchEvents();

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Deleted all appointments',
                        life: 3000
                    });
                } catch (error) {
                    console.error('[Appointments][deleteAppointmentAll] Error:', error);

                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to delete all appointments. Please try again.',
                        life: 3000
                    });
                }
            }

            this.showDialog = false;
            this.view = "display";
            this.submitted = false;
        },
        async deleteAppointmentFuture() {
            console.log("[" + name + "][deleteAppointmentFuture] Start.");

            this.submitted = true;

            if (this.appointment.id) {

                try {

                    const response = await AppointmentService.deleteAppointmentFuture(this.appointment.id);
                    console.log('[Appointments][deleteAppointmentFuture] Delete response = ', response);

                    this.showDialog = false;

                    // this.initCalendar();
                    this.$refs.calendarRef?.getApi().refetchEvents();

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Deleted future appointments',
                        life: 3000
                    });
                } catch (error) {
                    console.error('[Appointments][deleteAppointmentFuture] Error:', error);

                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to delete future appointments. Please try again.',
                        life: 3000
                    });
                }
            }

            this.showDialog = false;
            this.view = "display";
            this.submitted = false;
        },
        async manageClient() {
            console.log("[Calendar][manageClient] this.appointment = ", this.appointment);
            this.$router.push({name: 'commonClients', query: {inputClientId: this.appointment.clientId}});
        },
        async viewInvoice() {
            console.log("[Calendar][viewInvoice] this.appointment = ", this.appointment);
            this.$router.push({name: 'commonFinancialInvoices', query: {inputInvoiceId: this.appointment.invoiceId}});
        },
        sleep(ms) {
            return new Promise(res => setTimeout(res, ms));
        },
        async generateInvoice() {
            console.log("[Calendar][generateInvoice] Start.");
            console.log("[Calendar][generateInvoice] this.appointment = ", this.appointment);

            if (this.submitted) return;

            // await this.sleep(10000);

            this.submitted = true;

            if (this.appointment.id) {

                try {

                    if (this.appointment.type !== 'P') {

                        var tempBillingPlan = this.appointment.billingPlanName;
                        console.log("[Calendar][generateInvoice] BEFORE. tempBillingPlan = ", tempBillingPlan);

                        try {
                            if (this.appointment.billingPlanName.id == null) {
                                tempBillingPlan = this.appointment.billingPlanId;
                            } else {
                                tempBillingPlan = this.appointment.billingPlanName.id;
                            }

                        } catch (error) {
                            tempBillingPlan = this.appointment.billingPlanId;
                        }
                        console.log("[Calendar][generateInvoice] AFTER. tempBillingPlan = ", tempBillingPlan);

                        var tempBankAccount = this.invoiceOptions.bankAccount;
                        console.log("[Calendar][generateInvoice] BEFORE. tempBankAccount = ", tempBankAccount);

                        try {
                            if (this.invoiceOptions.bankAccount.id == null) {
                                tempBankAccount = this.invoiceOptions.bankAccountId;
                            } else {
                                tempBankAccount = this.invoiceOptions.bankAccount.id;
                            }

                        } catch (error) {
                            tempBankAccount = this.invoiceOptions.bankAccountId;
                        }

                        if (this.invoiceOptions.invoiced) {

                            console.log("[Calendar][generateInvoice] this.appointment = ", this.appointment);

                            this.invoiceOptions.autoEmailAndClaim = 'N';
                            this.invoiceOptions.autoAllocatePayment = 'N';
                            // this.invoiceOptions.invoiced = this.appointment.scheduled;

                            var newInvoiceDto = {
                                type: this.invoiceOptions.type,
                                invoiced: moment(this.invoiceOptions.invoiced).format('YYYY-MM-DD HH:mm'),
                                startDate: moment(this.invoiceOptions.startDate).format('YYYY-MM-DD HH:mm'),
                                endDate: moment(this.invoiceOptions.endDate).format('YYYY-MM-DD HH:mm'),
                                userId: this.profile.id,
                                billingPlanId: tempBillingPlan,
                                bankAccountId: tempBankAccount,
                                appointmentId: this.appointment.id,
                                clientId: this.appointment.clientId,
                                submissionMethod: this.invoiceOptions.submissionMethod,
                                healthInsuranceSubmission: this.invoiceOptions.healthInsuranceSubmission,
                                useSystemProcedureRates: this.invoiceOptions.useSystemProcedureRates,
                                useSystemProductRates: 'N',
                                useTaxRate: this.invoiceOptions.useTaxRate,
                                useAutomaticSubmission: this.invoiceOptions.useAutomaticSubmission,
                                rateOption: this.invoiceOptions.rateOption
                            };

                            if (this.companyConfiguration.type !== 'H') {
                                newInvoiceDto.submissionMethod = 'C';
                                newInvoiceDto.healthInsuranceSubmission = 'E';
                                newInvoiceDto.useSystemProcedureRates = 'N';
                                newInvoiceDto.useSystemProductRates = 'N';
                            }

                            console.log("[Calendar][generateInvoice] Before creating invoice back-end call. Data = ", newInvoiceDto);

                            const response = await InvoiceService.createInvoice(this.profile.companyId, newInvoiceDto);
                            console.log('[Calendar][generateInvoice] Delete response = ', response);

                            this.showDialog = false;

                            // this.initCalendar();
                            this.$refs.calendarRef?.getApi().refetchEvents();

                            const invoicesData = await InvoiceService.getAllCompanyInvoices(this.profile.companyId);
                            this.userStore.setInvoices(invoicesData.data);

                            this.$toast.add({
                                severity: 'success',
                                summary: 'Successful',
                                detail: 'Updated appointment',
                                life: 3000
                            });

                        }

                    }

                } catch (error) {
                    console.error('[Calendar][generateInvoice] Error:', error);

                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to update appointment. Please try again.',
                        life: 3000
                    });
                } finally {
                    this.submitted = false;          // ALWAYS reset
                }
            }

            this.showDialog = false;
            this.view = "display";
            this.submitted = false;
        }
    }
}
;
</script>

<style scoped>
a {
    text-decoration: none;
}

:deep(.fc-header-toolbar) {
    .fc-button {
        line-height: 1;
        min-height: 2.07rem;
    }
}

.legend {
    margin: 20px 0;
}

.legend h3 {
    margin-bottom: 10px;
}

.legend ul {
    list-style: none;
    padding: 0;
}

.legend li {
    display: flex;
    align-items: center;
    margin-bottom: 5px;
}

.color-box {
    display: inline-block;
    width: 20px;
    height: 20px;
    margin-right: 10px;
    border-radius: 4px;
}

.color-box.blue {
    background-color: #4495f1;
}

.color-box.magenta {
    background-color: magenta;
}

.color-box.green {
    background-color: #78ce74;
}

.color-box.red {
    background-color: red;
}

.color-box.orange {
    background-color: orange;
}

/* Define additional classes if needed */
.color-box.yellow {
    background-color: yellow;
}

.color-box.brown {
    background-color: brown;
}

.color-box.purple {
    background-color: #cc8aba;
}

.color-box.pink {
    background-color: pink;
}

.color-box.teal {
    background-color: teal;
}

.color-box.black {
    background-color: black;
}

.color-box.default1 {
    background-color: #A42BEAFF;
}

.color-box.default2 {
    background-color: #74e3c5;
}

.color-box.defaultColor {
    background-color: #55c2da;
}

</style>
